import React, { useState, useMemo, useEffect } from "react";
import { cms as axios } from "@core/axios";
import { connect } from "react-redux";
import {
	YMaps,
	Map,
	Placemark,
	GeolocationControl,
	ZoomControl,
	Clusterer
} from "react-yandex-maps";

import "./map-of-branches.scss";
import shadow from "@img/shadow.png";
import defaultImg from "./img/default.png";

import {
	MapControls,
	MapListTypeBranch
} from "@components/maps/map-of-branches/components";

import { transformPoints } from "./utils";

const MapWithSelectComponent = props => {
	const {
		defaultType,
		defaultCompany,
		showMapControls = true,
		city,
		cities,
		companies: { list, icons } = {}
	} = props;

	const [dataPoints, setDataPoints] = useState([]);
	const [dataCompanies, setDataCompanies] = useState([]);

	useEffect(() => {
		let arr = [];
		if (list && Object.keys(list).length) {
			const listToArr = Object.values(list);
			if (!defaultCompany) {
				arr = listToArr.map(item => {
					item.isActive = true;
					return item;
				});
			} else {
				arr = listToArr.map(item => {
					item.isActive = !!(
						item.name === defaultCompany && item.type === defaultType
					);
					return item;
				});
			}
			setDataCompanies(arr);
		} else {
			setDataCompanies([]);
		}
	}, [list, defaultType, defaultCompany]);

	/** Типы отделений */
	const types = [
		{
			id: 1,
			title: "Где пополнить",
			code: "refill"
		},
		{
			id: 2,
			title: "Где купить",
			code: "sale"
		}
	];

	/** Определяет текущий тип */
	const [currentType, setCurrentType] = useState(
		defaultType
			? types.find(type => type.code === defaultType) || types[0]
			: types && types[0]
	);

	/** Получает точки */
	useEffect(() => {
		const getPoints = async () => {
			const res = await axios("/points?_limit=1000");
			const points = transformPoints(res.data, icons);
			setDataPoints(points);
		};
		icons && getPoints();
	}, [icons]);

	/** Сортирует компании */
	const companies = useMemo(() => {
		let res = dataCompanies;
		if (res && res.length && currentType) {
			res = res.filter(company => company.type === currentType.code);
		}
		return res;
	}, [dataCompanies, currentType]);

	/** Сортирует точки */
	const points = useMemo(() => {
		let res = dataPoints;
		if (currentType && dataPoints.length) {
			res = res.filter(point => point[currentType.code]);
		}
		if (city && dataPoints.length) {
			res = res.filter(point => point.city === city);
		}
		if (companies && companies.length) {
			res = res.filter(point => {
				let res;
				for (let value of companies) {
					if (point.company === value.name && value.isActive) {
						res = true;
						break;
					}
				}
				return res;
			});
		}
		return res;
	}, [currentType, city, dataPoints, companies]);

	/** Конфиг карты */
	const [center, setCenter] = useState([]);
	const mapState = useMemo(
		() => ({
			center,
			zoom: 11,
			controls: ["fullscreenControl"]
		}),
		[center]
	);

	useEffect(() => {
		if (city) {
			// Выставляет позицию на карте согласно выбранному городу
			const { geo_position: { latitude, longitude } = {} } = cities.find(
				({ name }) => name === city
			);
			latitude && longitude && setCenter([latitude, longitude]);
		}
	}, [city, cities]);

	return (
		<section className="map-with-select">
			{showMapControls && companies && !!companies.length && (
				<div className="grid-container hidden-md">
					<div className="row">
						<div className="col-xs-12 col-md-offset-2 col-md-8 col-sm-offset-1 col-sm-10">
							<MapListTypeBranch
								types={types}
								setCurrentType={setCurrentType}
								currentType={currentType}
							/>
						</div>
					</div>
				</div>
			)}
			<div className="map-with-select__ymaps">
				<YMaps>
					<Map
						className="map-with-select__ymaps__map"
						state={mapState}
						modules={["control.FullscreenControl"]}
						instanceRef={ref => {
							ref && ref.behaviors.disable("scrollZoom");
						}}
					>
						<ZoomControl
							options={{
								position: {
									right: 10,
									top: 108
								}
							}}
						/>
						<GeolocationControl options={{ float: "right" }} />
						<Clusterer
							modules={["clusterer.addon.balloon"]}
							options={{
								preset: "islands#invertedDarkGreenClusterIcons",
								groupByCoordinates: false,
								clusterDisableClickZoom: false,
								clusterHideIconOnBalloonOpen: false,
								geoObjectHideIconOnBalloonOpen: false,
								zoomMargin: [200, 200, 200, 200]
							}}
						>
							{points &&
								points.length &&
								points.map(
									({ id, title, workTime, placeMark, phone, image }) =>
										placeMark && (
											<Placemark
												key={id}
												modules={["geoObject.addon.balloon"]}
												geometry={placeMark}
												onClick={() => {
													setCenter(placeMark);
												}}
												properties={{
													balloonContentHeader: title,
													balloonContentBody: phone && phone.join(",<br/>"),
													balloonContentFooter:
														workTime && workTime.join(",<br/>"),
													clusterCaption: title
												}}
												options={{
													iconLayout: "default#image",
													iconImageHref: image || defaultImg,
													iconImageSize: [32, 32],
													iconImageOffset: [-16, -8],
													iconShadow: true,
													iconShadowImageHref: shadow,
													iconShadowImageSize: [42, 42],
													iconShadowImageOffset: [-16, -8],
													hideIconOnBalloonOpen: false
												}}
											/>
										)
								)}
						</Clusterer>
					</Map>
				</YMaps>
			</div>
			{showMapControls && companies && !!companies.length && (
				<MapControls
					currentType={currentType}
					types={types}
					companies={companies}
					setCurrentType={setCurrentType}
					dataCompanies={dataCompanies}
					setDataCompanies={setDataCompanies}
				/>
			)}
		</section>
	);
};

const mapStateToProps = props => {
	const {
		city,
		staticData: { cities, companiesForMap: companies } = {}
	} = props;
	return { city, cities, companies };
};

const MapOfBranches = connect(
	mapStateToProps,
	null
)(MapWithSelectComponent);

export default MapOfBranches;
